// src/pages/Sanitary/SanitaryWorkPatternsPage.jsx
// =============================================================================
// Sanitary work patterns
// =============================================================================

import React, { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import AuthContext from "../../context/AuthContext";
import {
  createSanitaryWorkPattern,
  listSanitaryWorkPatterns,
  updateSanitaryWorkPattern,
} from "../../services/sanitaryWorkPatternService";

import "../Stock/StockMasterDataPage.css";
import "./SanitaryProductsPage.css";

const ISO_DAYS = [1, 2, 3, 4, 5, 6, 7];
const todayIso = () => new Date().toISOString().slice(0, 10);

function normalizeRole(role) {
  return String(role || "").trim().toLowerCase();
}

function defaultDays() {
  return ISO_DAYS.map((day) => ({
    day_of_week: day,
    is_working_day: day >= 1 && day <= 5,
    operating_minutes: day >= 1 && day <= 5 ? 480 : 0,
  }));
}

function emptyForm() {
  return {
    id: null,
    name: "",
    description: "",
    base_daily_minutes: 480,
    valid_from: todayIso(),
    valid_to: "",
    is_default: true,
    is_active: true,
    days: defaultDays(),
  };
}

function formFromPattern(pattern) {
  if (!pattern) return emptyForm();
  return {
    id: pattern.id,
    name: pattern.name || "",
    description: pattern.description || "",
    base_daily_minutes: pattern.base_daily_minutes || 480,
    valid_from: pattern.valid_from || todayIso(),
    valid_to: pattern.valid_to || "",
    is_default: Boolean(pattern.is_default),
    is_active: pattern.is_active !== false,
    days: ISO_DAYS.map((dow) => {
      const row = (pattern.days || []).find((day) => Number(day.day_of_week) === dow);
      return {
        day_of_week: dow,
        is_working_day: Boolean(row?.is_working_day),
        operating_minutes: Number(row?.operating_minutes || 0),
      };
    }),
  };
}

function minutesToHours(minutes) {
  const n = Number(minutes || 0);
  if (!Number.isFinite(n) || n <= 0) return "0 h";
  const hours = n / 60;
  return `${hours.toLocaleString(undefined, { maximumFractionDigits: 2 })} h`;
}

function dayLabel(t, dow) {
  return t(`sanitary:workPatterns.days.${dow}`, { defaultValue: String(dow) });
}

function patternSummary(pattern) {
  const days = pattern?.days || [];
  const total = days.reduce((sum, day) => sum + Number(day.operating_minutes || 0), 0);
  const workingDays = days.filter((day) => day.is_working_day && Number(day.operating_minutes || 0) > 0).length;
  return { total, workingDays };
}

export default function SanitaryWorkPatternsPage() {
  const { t } = useTranslation(["sanitary", "common"]);
  const { authState } = useContext(AuthContext);
  const role = normalizeRole(authState?.user?.role);
  const canEdit = role === "superuser" || role === "reception";

  const [patterns, setPatterns] = useState([]);
  const [selectedPattern, setSelectedPattern] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const selectedSummary = useMemo(() => patternSummary(selectedPattern), [selectedPattern]);

  async function loadPatterns() {
    setLoading(true);
    setError("");
    try {
      const rows = await listSanitaryWorkPatterns({ limit: 200 });
      setPatterns(rows);
      if (!selectedPattern && rows.length) {
        setSelectedPattern(rows[0]);
        setForm(formFromPattern(rows[0]));
      }
    } catch (err) {
      setError(err?.message || t("sanitary:workPatterns.messages.loadFailed"));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadPatterns();
  }, []);

  function patchForm(partial) {
    setForm((prev) => ({ ...prev, ...partial }));
  }

  function patchDay(dow, partial) {
    setForm((prev) => ({
      ...prev,
      days: prev.days.map((day) => {
        if (Number(day.day_of_week) !== Number(dow)) return day;
        const next = { ...day, ...partial };
        if (partial.is_working_day === false) next.operating_minutes = 0;
        if (partial.is_working_day === true && Number(next.operating_minutes || 0) <= 0) next.operating_minutes = Number(prev.base_daily_minutes || 480);
        return next;
      }),
    }));
  }

  function selectPattern(pattern) {
    setSelectedPattern(pattern);
    setForm(formFromPattern(pattern));
    setMessage("");
    setError("");
  }

  function startNewPattern() {
    setSelectedPattern(null);
    setForm(emptyForm());
    setMessage("");
    setError("");
  }

  async function handleSave(event) {
    event.preventDefault();
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const saved = form.id
        ? await updateSanitaryWorkPattern(form.id, form)
        : await createSanitaryWorkPattern(form);
      setSelectedPattern(saved);
      setForm(formFromPattern(saved));
      setMessage(t("sanitary:workPatterns.messages.saved"));
      await loadPatterns();
    } catch (err) {
      setError(err?.message || t("sanitary:workPatterns.messages.saveFailed"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <main className="stock-md-page sanitary-page">
      <header className="stock-md-header">
        <div>
          <p className="stock-md-eyebrow">{t("sanitary:workPatterns.eyebrow")}</p>
          <h1 className="stock-md-title">{t("sanitary:workPatterns.title")}</h1>
          <p className="stock-md-subtitle">{t("sanitary:workPatterns.subtitle")}</p>
        </div>
        <button type="button" className="stock-md-button" onClick={loadPatterns} disabled={loading || saving}>
          {t("common:actions.refresh", { defaultValue: "Refresh" })}
        </button>
      </header>

      <section className="stock-md-info-box">{t("sanitary:workPatterns.guidance")}</section>

      {error && <div className="stock-md-error">{error}</div>}
      {message && <div className="stock-md-success">{message}</div>}
      {!canEdit && <div className="stock-md-info-box">{t("sanitary:workPatterns.messages.readOnly")}</div>}

      <section className="stock-md-grid stock-md-grid--two stock-md-grid--top">
        <div className="stock-md-card">
          <div className="stock-md-card-header">
            <div>
              <h2>{t("sanitary:workPatterns.list.title")}</h2>
              <p>{t("sanitary:workPatterns.list.subtitle")}</p>
            </div>
            <button type="button" className="stock-md-button stock-md-button--secondary" onClick={startNewPattern} disabled={!canEdit || saving}>
              {t("sanitary:workPatterns.actions.new")}
            </button>
          </div>

          <div className="stock-md-table-wrap">
            <table className="stock-md-table">
              <thead>
                <tr>
                  <th>{t("sanitary:workPatterns.table.pattern")}</th>
                  <th>{t("sanitary:workPatterns.table.validity")}</th>
                  <th>{t("sanitary:workPatterns.table.week")}</th>
                  <th>{t("sanitary:workPatterns.table.status")}</th>
                  <th>{t("sanitary:workPatterns.table.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="5">{t("sanitary:workPatterns.messages.loading")}</td></tr>
                ) : patterns.length === 0 ? (
                  <tr><td colSpan="5">{t("sanitary:workPatterns.list.empty")}</td></tr>
                ) : patterns.map((pattern) => {
                  const summary = patternSummary(pattern);
                  return (
                    <tr key={pattern.id} className={selectedPattern?.id === pattern.id ? "is-selected" : ""}>
                      <td>
                        <strong>{pattern.name}</strong>
                        <small>{pattern.is_default ? t("sanitary:workPatterns.labels.default") : ""}</small>
                      </td>
                      <td>{pattern.valid_from} — {pattern.valid_to || "∞"}</td>
                      <td>{summary.workingDays} / {minutesToHours(summary.total)}</td>
                      <td>{pattern.is_active ? t("sanitary:workPatterns.statuses.active") : t("sanitary:workPatterns.statuses.inactive")}</td>
                      <td>
                        <button type="button" className="stock-md-button stock-md-button--small" onClick={() => selectPattern(pattern)}>
                          {t("sanitary:workPatterns.actions.select")}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="stock-md-card">
          <h2>{form.id ? t("sanitary:workPatterns.edit.title") : t("sanitary:workPatterns.create.title")}</h2>
          {selectedPattern ? (
            <div className="sanitary-profile-summary">
              <strong>{selectedPattern.name}</strong>
              <span>{t("sanitary:workPatterns.details.weeklyMinutes", { minutes: selectedSummary.total, hours: minutesToHours(selectedSummary.total) })}</span>
            </div>
          ) : null}

          <form onSubmit={handleSave}>
            <div className="stock-md-grid stock-md-grid--two">
              <label className="stock-md-field">
                <span>{t("sanitary:workPatterns.form.name")}</span>
                <input value={form.name} onChange={(e) => patchForm({ name: e.target.value })} disabled={!canEdit || saving} required />
              </label>
              <label className="stock-md-field">
                <span>{t("sanitary:workPatterns.form.baseDailyMinutes")}</span>
                <input type="number" min="1" max="1440" value={form.base_daily_minutes} onChange={(e) => patchForm({ base_daily_minutes: e.target.value })} disabled={!canEdit || saving} required />
                <small>{t("sanitary:workPatterns.form.baseDailyMinutesHelp")}</small>
              </label>
              <label className="stock-md-field">
                <span>{t("sanitary:workPatterns.form.validFrom")}</span>
                <input type="date" value={form.valid_from} onChange={(e) => patchForm({ valid_from: e.target.value })} disabled={!canEdit || saving} required />
              </label>
              <label className="stock-md-field">
                <span>{t("sanitary:workPatterns.form.validTo")}</span>
                <input type="date" value={form.valid_to || ""} onChange={(e) => patchForm({ valid_to: e.target.value })} disabled={!canEdit || saving} />
              </label>
              <label className="stock-md-field stock-md-field--wide">
                <span>{t("sanitary:workPatterns.form.description")}</span>
                <input value={form.description || ""} onChange={(e) => patchForm({ description: e.target.value })} disabled={!canEdit || saving} />
              </label>
            </div>

            <div className="stock-md-checkbox-row">
              <label className="stock-md-checkbox">
                <input type="checkbox" checked={form.is_default} onChange={(e) => patchForm({ is_default: e.target.checked })} disabled={!canEdit || saving} />
                {t("sanitary:workPatterns.form.isDefault")}
              </label>
              <label className="stock-md-checkbox">
                <input type="checkbox" checked={form.is_active} onChange={(e) => patchForm({ is_active: e.target.checked })} disabled={!canEdit || saving} />
                {t("sanitary:workPatterns.form.isActive")}
              </label>
            </div>

            <h3 className="stock-md-section-title">{t("sanitary:workPatterns.days.title")}</h3>
            <div className="stock-md-table-wrap">
              <table className="stock-md-table">
                <thead>
                  <tr>
                    <th>{t("sanitary:workPatterns.days.day")}</th>
                    <th>{t("sanitary:workPatterns.days.working")}</th>
                    <th>{t("sanitary:workPatterns.days.minutes")}</th>
                    <th>{t("sanitary:workPatterns.days.hours")}</th>
                  </tr>
                </thead>
                <tbody>
                  {form.days.map((day) => (
                    <tr key={day.day_of_week}>
                      <td>{dayLabel(t, day.day_of_week)}</td>
                      <td>
                        <input
                          type="checkbox"
                          checked={Boolean(day.is_working_day)}
                          onChange={(e) => patchDay(day.day_of_week, { is_working_day: e.target.checked })}
                          disabled={!canEdit || saving}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          min="0"
                          max="1440"
                          value={day.operating_minutes}
                          onChange={(e) => patchDay(day.day_of_week, { operating_minutes: e.target.value })}
                          disabled={!canEdit || saving || !day.is_working_day}
                        />
                      </td>
                      <td>{minutesToHours(day.operating_minutes)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="stock-md-actions">
              <button type="submit" className="stock-md-button stock-md-button--primary" disabled={!canEdit || saving}>
                {saving ? t("sanitary:workPatterns.messages.saving") : t("sanitary:workPatterns.actions.save")}
              </button>
            </div>
          </form>
        </div>
      </section>
    </main>
  );
}
